/**
 * What Qbee offers to answer on each page, before anyone types: three to five
 * questions a person on that page actually asks, each one Qbee can answer
 * from the data tools or the guides (routes/ask). Matched on the longest
 * route prefix, with the tab or section when the page has them; every other
 * page gets the general set.
 *
 * Pure data, no JSX, so a test can check every route here is a real one.
 */

const GENERAL = [
  'What does this page show, and how do I read it?',
  'How did the store do in the last 28 days?',
  'Which channel brought the most revenue this month?',
];

export const QBEE_SUGGESTIONS = {
  '/dashboard': [
    'How did the business do in the last 28 days compared with before?',
    'What does MER mean, and how is it worked out?',
    'Why could revenue here differ from Shopify?',
    'Which channel brought the most new customers this month?',
  ],
  '/growth/actions': ['What should I do first today, and why?', 'How are these actions chosen?', 'Which campaigns are losing money?'],
  '/growth/findings': ['What did Cookiebees find this week?', 'How are these findings worked out?'],
  '/growth/customer-value': ['What is a customer worth over time here?', 'How is customer value worked out?', 'Which channel brings the most valuable customers?'],
  '/growth/buyers': ['Who bought this month, new or returning?', 'How do I read this page?'],
  '/growth/leaks': ['Where am I losing the most money?', 'How is each leak measured?', 'What should I fix first?'],
  '/growth/channel-roles': ['Which channels start sales and which close them?', 'What do the channel roles mean?'],
  '/growth/creatives': ['Which ads brought the most revenue?', 'How are creatives matched to sales?'],
  '/growth/campaigns': ['Which campaign had the best ROAS last month?', 'Which campaigns brought repeat buyers?', 'How is a sale credited to a campaign?'],
  '/commerce': ['How many orders came from Meta ads this month?', 'What do the order columns mean?', 'Why does an order show a different currency?'],
  '/commerce/orders': ['How do I read the path to purchase?', 'What does match quality mean on an order?', 'Why does this order show a different currency?', 'What did Cookiebees send to Meta for an order?'],
  '/commerce/customers': ['How many customers came back to buy again?', 'How are customers matched across devices?'],
  '/commerce/abandoned': ['How many checkouts were abandoned in the last 7 days?', 'How do I recover abandoned checkouts?'],
  '/commerce/insights': ['Which channel brought the most revenue this month?', 'What is the difference between the attribution models?', 'How do I read this report?'],
  '/commerce/segments': ['Which customer groups are worth the most?', 'How do I build a segment?'],
  '/commerce/audiences': ['Why did this list stop refreshing?', 'Which lists should I exclude from prospecting?'],
  '/commerce/costs': ['What are my ad costs this month?', 'How do costs feed profit and MER?'],
  '/commerce/recovery': ['How much did recovery win back this month?', 'How does WhatsApp recovery work?'],
  '/studio?tab=connect': [
    'Which way of sending to Meta suits my store?',
    'What is the difference between browser and server events?',
    'Will choosing Cookiebees sends everything count sales twice?',
    'What does Purchase_cb mean?',
  ],
  '/studio?tab=overview': ['What does my store send to Meta and Google?', 'Which smart goal should I switch on next?'],
  '/studio?tab=goals': ['Which smart goal should I start with?', 'What is a custom conversion and how do I make one?', 'Why does a goal need 50 a week?'],
  '/studio?tab=events': ['Which events are going to Meta right now?', 'How do I send an event to Google Ads too?', 'What does Set it up do?'],
  '/studio?tab=params': ['Which parameters should I switch on?', 'What does coverage mean here?'],
  '/studio?tab=value': ['Should I send revenue or profit as the sale value?', 'How is profit after costs worked out?'],
  '/studio?tab=audiences': ['Which audience should I send to Meta first?', 'How do I build the audience in Meta from these events?', 'Why does an audience need 300 people?'],
  '/studio?tab=ads': ['What does Meta still not report from my store?', 'Which custom conversions should I create first?', 'Should my ads optimise for delivered orders or profit?'],
  '/studio?tab=checklist': ['Are my events carrying what Meta needs?', 'How do I raise match quality?'],
  '/studio': ['What does Cookiebees send to my ad platforms?', 'How do the parts of Signal Studio fit together?', 'How many conversions went to Meta this month?'],
  '/tracking/stream': ['Is everything reaching Meta and Google?', 'What does Held mean in the event stream?', 'How do I switch sending on or off?'],
  '/tracking/health': ['Are my signals healthy right now?', 'How is signal health measured?'],
  '/tracking/attribution': ['Which lead source converts best?', 'How is a lead credited to a channel?'],
  '/integrations': ['Which integrations are working?', 'How do I connect Meta?', 'What does Silent mean on an integration?'],
  '/settings': ['Where do I change the currency and time zone?', 'How do I add a team member?'],
  '/automations': ['What can a workflow do?', 'How do I send a purchase to Meta only for prepaid orders?'],
  '/leads': ['How many leads came in this month, and from where?', 'How is lead quality scored?'],
};

/** The suggestions for a route ("/studio?tab=setup&days=28" finds the setup set). */
export function qbeeSuggestions(pathname, search = '') {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  const q = new URLSearchParams(String(search || ''));
  let best = null; let bestScore = -1;
  for (const key of Object.keys(QBEE_SUGGESTIONS)) {
    const [kp, kq] = key.split('?');
    if (!(p === kp || p.startsWith(`${kp}/`))) continue;
    let score = kp.length;
    if (kq) {
      const want = new URLSearchParams(kq);
      let ok = true;
      for (const [k, v] of want) if (q.get(k) !== v) ok = false;
      if (!ok) continue;
      score += 1000;
    }
    if (score > bestScore) { best = key; bestScore = score; }
  }
  return best ? QBEE_SUGGESTIONS[best] : GENERAL;
}

export { GENERAL as QBEE_GENERAL };
